class HeaderSearch extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['close', 'input']
  }

  static get requiredExternalElements () {
    return ['trigger']
  }

  setupListeners () {
    super.setupListeners()

    this.handleToggle = this.handleToggle.bind(this)
    this.handleClose = this.handleClose.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handlePointerUp = this.handlePointerUp.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)

    this.externalEls.trigger.elements.forEach(triggerEl => {
      this.listeners.add(triggerEl, 'click', this.handleToggle)
      this.listeners.add(triggerEl, 'pointerup', this.handlePointerUp)
    })

    this.listeners.add(this.els.close.element, 'click', this.handleClose)
    this.listeners.add(this.els.form.element, 'submit', this.handleSubmit)

    // Clear results immediately on any input change (before debounced search)
    this.handleInputImmediate = this.handleInputImmediate.bind(this)
    this.listeners.add(
      this.els.input.element,
      'input',
      this.handleInputImmediate,
    )

    // Debounced search handler
    this.listeners.add(
      this.els.input.element,
      'input',
      window.BAO.Utils.debounce(event => {
        this.handleInput(event)
      }, 500),
    )

    this.listeners.add(document, 'click', this.handleClick)

    this.setupDatalayerTracking()
  }

  /**
   * Resets the search to its initial state.
   * Clears input, filters, search term, and products.
   */
  resetSearch () {
    this.els.input.element.value = ''
    this.handleQueryStateChange('')
    window.theme.autocompleteResults.stateLayer.state.activeFacets = []
    window.theme.autocompleteResults.searchTerm = ''

    // Use setter to trigger state:products:updated event for consistent DOM clearing
    window.theme.autocompleteResults.stateLayer.products = []

    // Also close the filter drawer if the search is closed.
    if (window.drawers.activeDrawerKey === 'tablet-collection-filters') {
      window.BAO.drawers.closeDrawer()
    }

    // Move the suggestions carousel back to the start.
    const quickLinks = document.querySelectorAll(
      'quick-links[data-type="autocomplete"]',
    )
    quickLinks.forEach(link => {
      link.setAttribute('data-reset', 'true')
    })

    this.els.results.element.scrollTop = 0
    this.bodyToggle(true)

    // Reset search scroll state in header-scroll
    const headerScroll = document.querySelector('header-scroll')
    if (headerScroll?.resetSearchScroll) {
      headerScroll.resetSearchScroll()
    }

    // Close all open facet summaries
    this.closeAllFacetSummaries()
  }

  handleSubmit (e) {
    e.preventDefault()

    this.els.input.element.blur()
  }

  /**
   * Immediately clears results when user starts typing/backspacing.
   * This happens before the debounced search to prevent showing old results.
   */
  handleInputImmediate (e) {
    const term = e.target.value
    const currentSearchTerm = window.theme.autocompleteResults?.searchTerm || ''

    // Don't clear results if the input is a quicklink input, to avoid a flash.
    if (e.target.hasAttribute('data-quicklink-input')) {
      return
    }

    // Always clear results immediately when user types/backspaces
    // This ensures old results never show, even if typing quickly
    if (term !== currentSearchTerm || term.length < 3) {
      // Clear results immediately - don't wait for debounced search
      window.theme.autocompleteResults.eventBus.publish(
        'search:clear:immediate',
      )

      // Hide results panel immediately if term is too short
      if (term.length < 3) {
        this.bodyToggle(true)
      }
    }
  }

  handleInput (e) {
    const term = e.target.value
    this.updateSearch(term)

    if (this.hasAttribute('data-quicklink-input')) {
      this.removeAttribute('data-quicklink-input')
    }
  }

  /**
   * Closes all open facet summary elements.
   */
  closeAllFacetSummaries () {
    const facetSummaries = document.querySelectorAll(
      'details[is="facet-summary"][open]',
    )
    facetSummaries.forEach(summary => {
      if (summary.close) {
        summary.close()
      } else {
        summary.removeAttribute('open')
      }
    })
  }

  /**
   * Updates the search with a new term.
   * Handles filter clearing for new searches and publishes search events.
   * @param {string} term - The search term
   */
  updateSearch (term) {
    this.els.results.element.scrollTop = 0

    if (term.length >= 3) {
      const url = new URL(window.location.href)
      const urlSearchTerm = url.searchParams.get('q')
      const currentSearchTerm =
        window.theme.autocompleteResults.searchTerm || ''
      const isNewSearch =
        urlSearchTerm !== term ||
        (currentSearchTerm !== '' && currentSearchTerm !== term)

      if (isNewSearch) {
        // New search - clear filters and publish event to update UI immediately
        window.theme.autocompleteResults.stateLayer.state.activeFacets = []
        window.theme.autocompleteResults.eventBus.publish(
          'state:activeFacets:updated',
          [],
        )
        url.searchParams.delete('search_filters')
        window.history.replaceState({}, '', url)
        sessionStorage.setItem('currentSearchTerm', term)
        // Close all open facet summaries
        this.closeAllFacetSummaries()
      }

      window.theme.autocompleteResults.searchTerm = term

      window.theme.autocompleteResults.eventBus.publish(
        'state:search:term',
        term,
      )

      this.bodyToggle(false)
    } else {
      this.bodyToggle(true)
      window.theme.autocompleteResults.stateLayer.state.activeFacets = []
      window.theme.autocompleteResults.eventBus.publish(
        'state:activeFacets:updated',
        [],
      )
      window.theme.autocompleteResults.searchTerm = ''
      // Close all open facet summaries when search term is cleared
      this.closeAllFacetSummaries()
    }

    this.handleQueryStateChange(term)
  }

  bodyToggle (state) {
    const body = this.querySelector('.hd-Search_Results')
    body.ariaHidden = state
    document.body.classList.toggle(this.autocompleteVisibleClassName, !state)
  }

  handlePointerUp () {
    this.focusInput()
  }

  handleToggle () {
    const searchClosed = this.getAttribute('aria-hidden') === 'true'

    if (searchClosed) {
      this.pushDatalayer({ event: 'search_open' })
    }

    this.setAttribute('aria-hidden', !searchClosed)

    document.body.classList.toggle(this.searchVisibleClassName, searchClosed)

    if (!searchClosed) {
      document.body.classList.remove(this.autocompleteVisibleClassName)
      this.resetSearch()
    }

    if (!searchClosed) return

    this.focusInput()
  }

  handleClose () {
    if (this.getAttribute('aria-hidden') === 'true') return

    this.setAttribute('aria-hidden', 'true')
    document.body.classList.remove(this.searchVisibleClassName)
    document.body.classList.remove(this.autocompleteVisibleClassName)
    this.resetSearch()
  }

  handleClick (event) {
    if (this.getAttribute('aria-hidden') === 'true') return

    if (event.target.closest(this.localName)) return
    if (event.target.closest(this.externalEls?.trigger?.selector)) return
    if (event.target.closest(this.externalEls?.collectionHeader?.selector)) {
      return
    }
    if (event.target.closest(this.els?.close?.selector)) return
    if (event.target.closest('[class*="klevu"]')) return
    if (event.target.closest('[class*="clc-ProductGridFacetsTablet"]')) return
    if (event.target.closest('[class*="clc-ProductGridFacetsMobile"]')) return
    if (event.target.closest('[class*="lpm-ListPicker"]')) return
    if (event.target.closest('[class*="clc-ProductGrid_Pagination"]')) return

    this.handleClose()
  }

  focusInput () {
    if (this.isiOS) {
      // Align temp input element approximately where the input element is
      // so the cursor doesn't jump around
      const tempInputEl = document.createElement('input')
      tempInputEl.style.position = 'absolute'
      tempInputEl.style.top = `${this.els.input.element.offsetTop + 7}px`
      tempInputEl.style.left = `${this.els.input.element.offsetLeft}px`
      tempInputEl.style.height = 0
      tempInputEl.style.opacity = 0
      // Put this temp element as a child of the page <body> and focus on it
      document.body.appendChild(tempInputEl)
      tempInputEl.focus()

      // The keyboard is open. Now do a delayed focus on the target element
      window.setTimeout(() => {
        this.els.input.element.focus()
        this.els.input.element.click()
        // Remove the temp element
        document.body.removeChild(tempInputEl)
      }, 500)
    } else {
      window.setTimeout(() => {
        this.els.input.element.focus()
      }, 25)
    }
  }

  /**
   * Updates the URL query parameters based on the search term.
   * @param {string} term - The search term
   */
  handleQueryStateChange (term) {
    const url = new URL(window.location.href)

    if (term) {
      url.searchParams.set('q', term)
    } else {
      url.searchParams.delete('q')
      url.searchParams.delete('search_filters')
    }

    url.searchParams.delete('auto_page')

    window.history.replaceState({}, '', url)
  }

  async sendSearchAnalytics (data) {
    const term = window.theme.autocompleteResults?.searchTerm || ''
    const apiKey = window.theme.collection?.klevuAPIKey
    const ipAddress = sessionStorage.getItem('klevu_ip_address')
    const uuid = sessionStorage.getItem('klevu_uuid')
    const totalResults = data?.queryResults?.[0]?.meta?.totalResultsFound ?? 0

    if (!term || !apiKey || !ipAddress) return
    if (this.lastAnalyticsTerm === term) return
    this.lastAnalyticsTerm = term

    const params = new URLSearchParams({
      klevu_term: term,
      klevu_apiKey: apiKey,
      klevu_totalResults: String(totalResults),
      klevu_typeOfQuery: 'search',
      klevu_shopperIP: ipAddress,
    })
    if (uuid) params.set('klevu_uuid', uuid)

    await fetch(
      `https://stats.ksearchnet.com/analytics/n-search/search?${params.toString()}`,
    )
  }

  pushDatalayer (payload) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(payload)
  }

  setupDatalayerTracking () {
    const eventBus = window.theme.autocompleteResults.eventBus

    eventBus.subscribe('state:search:term', term => {
      this.pushDatalayer({ event: 'search_term_entered', search_term: term })
    })

    eventBus.subscribe('api:products:fetched', data => {
      this.pushDatalayer({
        event: 'search_results_viewed',
        search_term: window.theme.autocompleteResults.searchTerm || '',
        total_results: data.queryResults[0].meta.totalResultsFound,
        page:
          window.theme.autocompleteResults.stateLayer.state.pagination
            .currentPage,
      })
      this.sendSearchAnalytics(data)
    })

    eventBus.subscribe('facets-list:apply-filter', ({ facet, option }) => {
      this.pushDatalayer({
        event: 'search_filter_applied',
        search_term: window.theme.autocompleteResults.searchTerm || '',
        facet_key: facet,
        facet_value: option,
      })
    })

    eventBus.subscribe('state:sort:updated', value => {
      this.pushDatalayer({
        event: 'search_sort_changed',
        search_term: window.theme.autocompleteResults.searchTerm || '',
        sort_value: value,
      })
    })

    // Capture phase so navigation triggered by the click can't interrupt the push
    this.handleProductClick = this.handleProductClick.bind(this)
    document.addEventListener('click', this.handleProductClick, {
      capture: true,
    })
  }

  handleProductClick (event) {
    const item = event.target.closest('[data-search-product-grid-el="item"]')
    if (!item) return

    const siblings = item.parentElement.querySelectorAll(
      '[data-search-product-grid-el="item"]',
    )

    this.pushDatalayer({
      event: 'search_product_clicked',
      search_term: window.theme.autocompleteResults.searchTerm || '',
      product_name: item.dataset.productName,
      position: Array.from(siblings).indexOf(item) + 1,
    })
  }

  get searchVisibleClassName () {
    return 'lyt-Header-searchVisible'
  }

  get autocompleteVisibleClassName () {
    return 'util-SiteOverflowed'
  }

  get isiOS () {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    )
  }
}

if (!customElements.get('header-search')) {
  customElements.define('header-search', HeaderSearch)
}
